import React, { useCallback, useEffect, useRef, useState } from "react";
import { Columns2, SlidersHorizontal, Contrast, Download, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { kgToLb, kgToStone, round1 } from "@/lib/units";

const BLUE = "#4C9AFF";
const PINK = "#FB7185";
const YELLOW = "#E2E30A";

export function fmtWeight(kg, unit = "kg") {
  if (kg == null) return null;
  if (unit === "lb") return `${round1(kgToLb(kg))} lb`;
  if (unit === "st") { const s = kgToStone(kg); return `${s.st}st ${s.lb}lb`; }
  return `${round1(kg)} kg`;
}

export function fmtWeightDelta(kg, unit = "kg") {
  if (kg == null) return null;
  const sign = kg > 0 ? "+" : kg < 0 ? "−" : "";
  if (unit === "lb") return `${sign}${round1(Math.abs(kgToLb(kg)))} lb`;
  if (unit === "st") {
    const totalLb = kgToLb(Math.abs(kg));
    const st = Math.floor(totalLb / 14);
    return `${sign}${st}st ${Math.round(totalLb - st * 14)}lb`;
  }
  return `${sign}${round1(Math.abs(kg))} kg`;
}

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined,
      { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const gapLabel = (a, b) => {
  const days = Math.abs(Math.round(
    (new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000));
  if (days === 0) return "Same day";
  if (days < 14) return `${days} day${days === 1 ? "" : "s"}`;
  if (days < 70) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30.4)} months`;
};

// Progress Overlay — drag the handle across two photos to reveal the change,
// or flip to side-by-side / fade. Exports a single shareable PNG.
export default function ProgressOverlay({
  before, after, urlFor, weightUnit = "kg", athleteName = "",
}) {
  const [mode, setMode] = useState("slider");     // slider | side | fade
  const [pos, setPos] = useState(50);             // reveal % (slider) / opacity % (fade)
  const [showWeight, setShowWeight] = useState(true);
  const [exporting, setExporting] = useState(false);
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = frameRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pct = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => { dragging.current = false; };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  if (!before || !after) return null;

  const deltaKg = (before.weightKg != null && after.weightKg != null)
    ? +(after.weightKg - before.weightKg).toFixed(1) : null;
  const gap = gapLabel(before.date, after.date);

  const startDrag = (e) => {
    dragging.current = true;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };

  const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  // Draw the comparison onto a canvas and download it. Mirrors whatever mode
  // is on screen, with the dates, the gap, optional weight change and the
  // SPC mark burned in so a shared image stands on its own.
  const saveImage = async () => {
    setExporting(true);
    try {
      const [bImg, aImg] = await Promise.all([
        loadImage(urlFor(before.photoPath)), loadImage(urlFor(after.photoPath)),
      ]);
      const W = 1080, H = 1440, FOOT = 190;
      const c = document.createElement("canvas");
      c.width = W; c.height = H + FOOT;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#0A0A09";
      ctx.fillRect(0, 0, W, H + FOOT);

      // cover-fit draw inside a clipped box
      const cover = (img, x, y, w, h) => {
        const s = Math.max(w / img.width, h / img.height);
        const dw = img.width * s, dh = img.height * s;
        ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
      };

      if (mode === "side") {
        const half = W / 2;
        ctx.save(); ctx.beginPath(); ctx.rect(0, 0, half, H); ctx.clip();
        cover(bImg, 0, 0, half, H); ctx.restore();
        ctx.save(); ctx.beginPath(); ctx.rect(half, 0, half, H); ctx.clip();
        cover(aImg, half, 0, half, H); ctx.restore();
        ctx.fillStyle = YELLOW; ctx.fillRect(half - 2, 0, 4, H);
      } else if (mode === "fade") {
        cover(bImg, 0, 0, W, H);
        ctx.globalAlpha = pos / 100;
        cover(aImg, 0, 0, W, H);
        ctx.globalAlpha = 1;
      } else {
        cover(bImg, 0, 0, W, H);
        const split = (pos / 100) * W;
        ctx.save();
        ctx.beginPath(); ctx.rect(split, 0, W - split, H); ctx.clip();
        cover(aImg, 0, 0, W, H);
        ctx.restore();
        ctx.fillStyle = YELLOW; ctx.fillRect(split - 2, 0, 4, H);
      }

      // Date pills
      const pill = (text, x, y, colour, align = "left") => {
        ctx.font = "bold 30px Helvetica, Arial, sans-serif";
        const w = ctx.measureText(text).width + 36;
        const px = align === "right" ? x - w : x;
        ctx.fillStyle = "rgba(0,0,0,0.72)";
        ctx.fillRect(px, y, w, 54);
        ctx.fillStyle = colour;
        ctx.fillRect(px, y, 6, 54);
        ctx.fillStyle = "#fff";
        ctx.textAlign = "left"; ctx.textBaseline = "middle";
        ctx.fillText(text, px + 24, y + 28);
      };
      pill(`BEFORE · ${fmtDate(before.date)}`, 32, 32, BLUE);
      pill(`AFTER · ${fmtDate(after.date)}`, W - 32, 32, PINK, "right");

      // Footer band
      ctx.fillStyle = "#111110";
      ctx.fillRect(0, H, W, FOOT);
      ctx.fillStyle = YELLOW;
      ctx.fillRect(0, H, W, 5);
      ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
      ctx.fillStyle = "#fff";
      ctx.font = "bold 58px Helvetica, Arial, sans-serif";
      const headline = showWeight && deltaKg != null
        ? `${gap} · ${fmtWeightDelta(deltaKg, weightUnit)}`
        : gap;
      ctx.fillText(headline.toUpperCase(), 40, H + 86);
      ctx.fillStyle = "#9b9b94";
      ctx.font = "26px Helvetica, Arial, sans-serif";
      const sub = [
        athleteName || null,
        `${(before.pose || "front").toUpperCase()} POSE`,
        showWeight && before.weightKg != null && after.weightKg != null
          ? `${fmtWeight(before.weightKg, weightUnit)} → ${fmtWeight(after.weightKg, weightUnit)}`
          : null,
      ].filter(Boolean).join("  ·  ");
      ctx.fillText(sub, 40, H + 130);
      ctx.textAlign = "right";
      ctx.fillStyle = YELLOW;
      ctx.font = "bold 24px Helvetica, Arial, sans-serif";
      ctx.fillText("PERFORMANCE NUTRITION", W - 40, H + 86);
      ctx.fillStyle = "#6f6f69";
      ctx.font = "20px Helvetica, Arial, sans-serif";
      ctx.fillText("POWERED BY SPC", W - 40, H + 120);

      const link = document.createElement("a");
      link.download = `progress-${before.date}-vs-${after.date}.png`;
      link.href = c.toDataURL("image/png");
      link.click();
      toast.success("Comparison image saved.");
    } catch {
      toast.error("Couldn't build the image — try again.");
    } finally {
      setExporting(false);
    }
  };

  const chip = (active) => `inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${active ? "bg-[#E2E30A] text-black" : "text-zinc-300 hover:text-white"}`;

  return (
    <div className="space-y-3" data-testid="progress-overlay">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex gap-1 border border-white/10 p-1" data-testid="progress-overlay-modes">
          <button type="button" data-testid="progress-overlay-mode-slider"
            onClick={() => { setMode("slider"); setPos(50); }} className={chip(mode === "slider")}>
            <SlidersHorizontal className="h-3.5 w-3.5" /> Slider
          </button>
          <button type="button" data-testid="progress-overlay-mode-side"
            onClick={() => setMode("side")} className={chip(mode === "side")}>
            <Columns2 className="h-3.5 w-3.5" /> Side by side
          </button>
          <button type="button" data-testid="progress-overlay-mode-fade"
            onClick={() => { setMode("fade"); setPos(50); }} className={chip(mode === "fade")}>
            <Contrast className="h-3.5 w-3.5" /> Fade
          </button>
        </div>
        <button type="button" data-testid="progress-overlay-toggle-weight"
          onClick={() => setShowWeight((v) => !v)}
          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-300 hover:text-white transition-colors">
          {showWeight ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {showWeight ? "Weight shown" : "Weight hidden"}
        </button>
        <button type="button" data-testid="progress-overlay-save" onClick={saveImage} disabled={exporting}
          className="ml-auto inline-flex items-center gap-1.5 bg-[#E2E30A] hover:bg-white disabled:opacity-60 text-black px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors">
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Save image
        </button>
      </div>

      {/* The comparison frame */}
      <div ref={frameRef} data-testid="progress-overlay-frame"
        className="relative aspect-[3/4] max-h-[62vh] mx-auto bg-black overflow-hidden select-none border border-white/10"
        style={{ touchAction: mode === "side" ? "auto" : "none",
                 cursor: mode === "side" ? "default" : "ew-resize" }}
        onMouseDown={mode === "side" ? undefined : startDrag}
        onTouchStart={mode === "side" ? undefined : startDrag}>
        {mode === "side" ? (
          <div className="absolute inset-0 grid grid-cols-2">
            <div className="relative overflow-hidden border-r-2 border-[#E2E30A]">
              <img src={urlFor(before.photoPath)} alt="before" className="w-full h-full object-cover" />
            </div>
            <div className="relative overflow-hidden">
              <img src={urlFor(after.photoPath)} alt="after" className="w-full h-full object-cover" />
            </div>
          </div>
        ) : (
          <>
            <img src={urlFor(before.photoPath)} alt="before"
              className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <img src={urlFor(after.photoPath)} alt="after" draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
              style={mode === "fade"
                ? { opacity: pos / 100 }
                : { clipPath: `inset(0 0 0 ${pos}%)` }} />
            {mode === "slider" && (
              <>
                <div className="absolute top-0 bottom-0 w-[3px] bg-[#E2E30A] pointer-events-none"
                  style={{ left: `${pos}%` }} />
                <div data-testid="progress-overlay-handle"
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-[#E2E30A] border-2 border-black grid place-items-center pointer-events-none"
                  style={{ left: `${pos}%` }}>
                  <SlidersHorizontal className="h-4 w-4 text-black" />
                </div>
              </>
            )}
          </>
        )}

        {/* Labels */}
        <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest px-2 py-1 bg-black/75 text-white border-l-2"
          style={{ borderColor: BLUE }}>
          Before · {fmtDate(before.date)}
          {showWeight && before.weightKg != null && <> · {fmtWeight(before.weightKg, weightUnit)}</>}
        </span>
        <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-widest px-2 py-1 bg-black/75 text-white border-r-2 text-right"
          style={{ borderColor: PINK }}>
          After · {fmtDate(after.date)}
          {showWeight && after.weightKg != null && <> · {fmtWeight(after.weightKg, weightUnit)}</>}
        </span>
        <span data-testid="progress-overlay-headline"
          className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-[#E2E30A] text-black">
          {gap}{showWeight && deltaKg != null ? ` · ${fmtWeightDelta(deltaKg, weightUnit)}` : ""}
        </span>
      </div>

      {mode !== "side" && (
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
            {mode === "fade" ? "Fade" : "Reveal"}
          </span>
          <input type="range" min="0" max="100" value={Math.round(pos)}
            data-testid="progress-overlay-range"
            onChange={(e) => setPos(Number(e.target.value))}
            className="flex-1 accent-[#E2E30A]" />
          <span className="text-[10px] font-mono text-zinc-400 w-10 text-right">{Math.round(pos)}%</span>
        </div>
      )}
      <p className="text-[10px] text-zinc-500">
        {mode === "side" ? "Two photos, straight comparison."
          : mode === "fade" ? "Drag to ghost the after photo over the before."
          : "Drag anywhere on the photo to sweep the line across."}
        {" "}Shoot from the same spot, same light and same pose for the cleanest comparison.
      </p>
    </div>
  );
}
